import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { updateSearchParams, useSearchParams } from '@ctw/shared/utils/search-params';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { type Dispatch, type SetStateAction, useCallback } from 'react';

const RISK_GAP_RESOURCE_TYPE_KEY = 'detailResourceType';
const RISK_GAP_RESOURCE_ID_KEY = 'detailResourceId';

/** State that we want to initialize and track inside of our search params for risk-gaps v2. */
export function useSearchParamsStateForRiskGaps(): [
  string | null,
  Dispatch<SetStateAction<string | null>>,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  // set common risk gap values in the window.location.search
  const updateResourceIdSearchParam = useCallback(
    (newResourceId: string | null) => {
      setSearchParams(
        (currentParams) => {
          if (newResourceId) {
            currentParams.set(RISK_GAP_RESOURCE_TYPE_KEY, 'MeasureReport');
            currentParams.set(RISK_GAP_RESOURCE_ID_KEY, newResourceId);
          } else {
            currentParams.delete(RISK_GAP_RESOURCE_TYPE_KEY);
            currentParams.delete(RISK_GAP_RESOURCE_ID_KEY);
          }
          return currentParams;
        },
        {
          // don't add more state to the navigation history.
          replace: true,
          // If we've scrolled to find the resource, attempt to keep it on screen.
          preventScrollReset: true,
        },
      );
    },
    [setSearchParams],
  );

  const selectedRiskGapId = searchParams.get(RISK_GAP_RESOURCE_ID_KEY);
  const setSelectedRiskGapId: Dispatch<SetStateAction<string | null>> = useCallback(
    (newValueOrFn: SetStateAction<string | null>) => {
      updateResourceIdSearchParam(
        typeof newValueOrFn === 'function' ? newValueOrFn(selectedRiskGapId) : newValueOrFn,
      );
    },
    [updateResourceIdSearchParam, selectedRiskGapId],
  );
  return [selectedRiskGapId, setSelectedRiskGapId];
}

/** Use this hook for navigating to the new risk gaps v2 experimental page */
export function useNavigateToRiskGapsV2(): (record: RiskGapModel) => void {
  const { navigate, pathname } = useZuiContext();
  const navigateToRiskGapV2 = useCallback(
    (record: RiskGapModel) => {
      // `navigate` re-joins an absolute path onto the app's base path (e.g. `/v1` in the embedded
      // ZAP), so that base path must be stripped from `pathname` first, or it ends up doubled. A
      // trailing slash is stripped too, or it survives the trim below and nests the destination
      // under the current segment instead of its sibling.
      const basePathStrippedPathname = pathWithParameters(pathname, {}).replace(/\/+$/, '');
      const patientRouteBase = basePathStrippedPathname.slice(
        0,
        basePathStrippedPathname.lastIndexOf('/'),
      );

      navigate(
        `${patientRouteBase}/risk-gaps?${updateSearchParams((currentParams) => {
          currentParams.set(RISK_GAP_RESOURCE_TYPE_KEY, record.type);
          currentParams.set(RISK_GAP_RESOURCE_ID_KEY, record.id);
          return currentParams;
        })}`,
      );
    },
    [navigate, pathname],
  );
  return navigateToRiskGapV2;
}
